/**
 * Правила событий: что и когда уходит в аналитику. Чистая логика без сети и DOM — вход: журналы волн, состояние игрока,
 * нажатие «поделиться». «Первое» — первое у игрока: флаг в localStorage плюс то, что сервер знает при входе (`bestWave`,
 * `PlayerHistory`), иначе второе устройство того же игрока считается новым.
 */
import {
  NO_HISTORY,
  TICK_RATE,
  type AnalyticsEvent,
  type DailyTaskClaim,
  type FeedbackType,
  type OfferPlace,
  type Platform,
  type PlayerHistory,
  type PlayerState,
  type Product,
  type PurchaseStatus,
  type ReviveOffer,
  type WaveLog,
} from "@newfungame/contracts";
import { dayNumber } from "./day";
import { createSessions } from "./session";

export interface TrackerDeps {
  track(event: AnalyticsEvent): void;
  now(): number;
  /** Флаги «уже отправляли». Без хранилища (приватный режим) — в памяти на сессию. */
  storage: Pick<Storage, "getItem" | "setItem"> | null;
  /** Случайный id сессии; не связан с игроком. */
  newId(): string;
}

const KEY = "nfg.a.";

/** Вехи воронки: шаг воронки Umami — имя события, поэтому у каждой своё. Волна 10 — это `first_boss`. */
const MILESTONES = [
  { wave: 5, name: "wave_5" },
  { wave: 20, name: "wave_20" },
] as const;

export function createTracker(deps: TrackerDeps) {
  const startedAt = deps.now();
  const sessions = createSessions(deps);
  const memory = new Set<string>();
  const seen = (flag: string): boolean => {
    if (memory.has(flag)) return true;
    try {
      return deps.storage?.getItem(KEY + flag) === "1";
    } catch {
      return false;
    }
  };
  const mark = (flag: string): void => {
    memory.add(flag);
    try {
      deps.storage?.setItem(KEY + flag, "1");
    } catch {
      /* хранилище недоступно — флаг живёт до конца сессии */
    }
  };
  /** Первый раз — true и флаг ставится. */
  const once = (flag: string): boolean => {
    if (seen(flag)) return false;
    mark(flag);
    return true;
  };

  /** Реванш применён, итог его волны ещё не пришёл. Только в памяти: игра заново после перезагрузки — обычная волна. */
  let revived: ReviveOffer | null = null;

  return {
    /** Один раз за сессию, когда известны платформа и источник. */
    appOpen(platform: Platform, startParam: string | null): void {
      deps.track({ name: "app_open", props: { platform, startParam } });
    },

    /** Журнал только что завершённой волны (`onWaveLog`). */
    waveLogged(log: WaveLog): void {
      const { outcome, bossKilled } = log.result;
      if (revived?.wave === log.wave) {
        const { wave, attempt } = revived;
        deps.track({ name: "revive_result", props: { wave, attempt, outcome } });
        revived = null;
      }
      if (outcome === "cleared" && once("first_wave")) {
        deps.track({
          name: "first_wave",
          props: { seconds: Math.max(0, Math.round((deps.now() - startedAt) / 1000)) },
        });
      }
      if (outcome === "lost" && once("first_defeat")) deps.track({ name: "first_defeat", props: { wave: log.wave } });
      if (bossKilled && once("first_boss")) deps.track({ name: "first_boss", props: { wave: log.wave } });
      deps.track({
        name: "wave_end",
        props: {
          wave: log.wave,
          result: outcome,
          seconds: Math.round(log.result.ticks / TICK_RATE),
          sessionSeconds: sessions.wave(),
        },
      });
      if (outcome !== "cleared") return;
      for (const { wave, name } of MILESTONES) {
        if (log.wave >= wave && once(name)) deps.track({ name, props: {} });
      }
    },

    /** Новое состояние игрока (вход, ответы сервера); `prev` — `null` при первом входе за сессию. */
    playerChanged(prev: PlayerState | null, next: PlayerState, history: PlayerHistory = NO_HISTORY): void {
      if (prev === null) {
        // новое устройство: что уже было у игрока, второй раз не «первое»
        if (next.bestWave > 0) mark("first_wave");
        if (history.bossKilled) mark("first_boss");
        if (history.lost) mark("first_defeat");
        for (const { wave, name } of MILESTONES) if (next.bestWave >= wave) mark(name);
        sessions.begin(next.player.createdAt);
        if (dayNumber(deps.now()) - dayNumber(next.player.createdAt) === 1 && once("return_day2")) {
          deps.track({ name: "return_day2", props: { bestWave: next.bestWave } });
        }
        return;
      }
      for (const hero of next.heroes) {
        if (!hero.unlocked || prev.heroes.find((h) => h.heroId === hero.heroId)?.unlocked !== false) continue;
        deps.track({
          name: "hero_unlocked",
          props: { heroId: hero.heroId, wave: Math.max(1, next.bestWave, next.run.wave) },
        });
      }
    },

    /** Игра свёрнута или снова на экране. */
    appHidden: sessions.hidden,
    appVisible: sessions.visible,
    /** Пульс сессии, раз в `BEAT_MS`. */
    sessionBeat: sessions.beat,

    /** Нажата кнопка «поделиться» (в чат или в сторис). */
    friendInvite(place: "boss" | "league"): void {
      deps.track({ name: "friend_invite", props: { place } });
    },
    share(target: "chat" | "story" | "link", wave: number): void {
      deps.track({ name: "share", props: { target, wave: Math.max(1, wave) } });
    },

    tasksOpen(claimable: number): void {
      deps.track({ name: "tasks_open", props: { claimable } });
    },

    taskClaim(task: DailyTaskClaim, coins: number, bestWave: number): void {
      deps.track({ name: "task_claim", props: { task, coins, bestWave } });
    },

    writeAccessAsk(wave: number): void {
      deps.track({ name: "write_access_ask", props: { wave: Math.max(1, wave) } });
    },

    writeAccess(allowed: boolean, via: "prompt" | "telegram"): void {
      deps.track({ name: "write_access", props: { allowed, via } });
    },

    homeScreenAsk(wave: number): void {
      deps.track({ name: "home_screen_ask", props: { wave: Math.max(1, wave) } });
    },

    parcelTap(wave: number, reward: "coins" | "boost", seconds: number) {
      deps.track({ name: "parcel_tap", props: { wave, reward, seconds } });
    },
    parcelMissed(wave: number) {
      deps.track({ name: "parcel_missed", props: { wave } });
    },
    feedbackSent(type: FeedbackType): void {
      deps.track({ name: "feedback_sent", props: { type } });
    },
    homeScreenAdded(wave: number): void {
      deps.track({ name: "home_screen_added", props: { wave: Math.max(1, wave) } });
    },

    offerView(place: OfferPlace): void {
      deps.track({ name: "offer_view", props: { place } });
    },

    reviveOffer({ wave, attempt, stars }: ReviveOffer): void {
      deps.track({ name: "revive_offer", props: { wave, attempt, stars } });
    },

    starterOffer(place: OfferPlace): void {
      deps.track({ name: "starter_offer", props: { place } });
    },

    /** Реванш применён: следующий журнал этой волны — его итог. */
    reviveStarted(offer: ReviveOffer): void {
      revived = offer;
    },

    purchaseClick(product: Product, place: OfferPlace, telegram: boolean): void {
      deps.track({ name: "purchase_click", props: { product, place, telegram } });
    },

    purchaseResult(product: Product, place: OfferPlace, status: PurchaseStatus): void {
      deps.track({ name: "purchase_result", props: { product, place, status } });
    },
  };
}

export type Tracker = ReturnType<typeof createTracker>;
